import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { MarketplaceListing, MarketplaceRate } from '../../models/apiTypes';
import type { OwnerInfo } from '../../models/mapObject';
import { formatUserName } from '../../utils/format/formatUserName';
import { openTelegramProfile } from '../../utils/telegram/openTelegramProfile';
import { getDecorImagePath } from '../../data/mapDecorAssets';
import FullscreenAssetViewer from '../primitives/FullscreenAssetViewer';
import { cn } from '../cn';


/**
 * Lighthouse popup — a standalone modal (composition, NOT inheriting ModalFrame's
 * header). ModalFrame always reserves a sticky header band even with no title,
 * which left a big empty gap above the lighthouse content. Here the card draws its
 * own full-screen backdrop + close button at the same abstraction level, so the
 * hero image starts right at the top.
 *
 * Purely presentational: all state/handlers live in MapCanvas and arrive as props.
 */

const GOLD = 'var(--color-lighthouse-modal-accent)';
const TEAL = 'var(--color-brand)';
const CARD_BG = 'var(--color-surface-base)';

interface LighthouseModalProps {
  isOpen: boolean;
  onClose: () => void;

  priceStars: number;
  owner: OwnerInfo | null;
  isOwned: boolean;
  isMine: boolean;

  // Buy with Stars (unowned)
  onBuy: () => void;
  buyLoading: boolean;
  hintOpen: boolean;
  onToggleHint: () => void;

  // Owner: sell / active listing
  listing: MarketplaceListing | null;
  sellOpen: boolean;
  onOpenSell: () => void;
  sellPrice: string;
  onChangeSellPrice: (v: string) => void;
  onList: () => void;
  sellLoading: boolean;
  onCancelListing: () => void;

  // Buyer (object is listed, viewer is not the owner)
  rate: MarketplaceRate | null;
  buyCurrency: 'ton' | 'usdt';
  onSetBuyCurrency: (c: 'ton' | 'usdt') => void;
  onWalletBuy: () => void;
  walletBuyLoading: boolean;
  walletConnected: boolean;
}

const ctaClassName =
  'w-full cursor-pointer rounded-[14px] border-none p-[14px] text-[16px] font-extrabold tracking-[0.2px] text-lighthouse-modal-text';

const TOOLTIP_BG = 'var(--color-lighthouse-modal-accent-2)';

/**
 * "?" help button with a robust popover. The tooltip is portaled to <body> and
 * positioned with fixed coordinates from the button's viewport rect, so it can
 * NEVER be clipped by the modal's overflow/scroll. It opens ABOVE the button by
 * default and only flips below when there isn't enough room above; horizontally
 * it is clamped to stay fully on-screen. This guarantees the hint is always
 * visible right next to the button — the user can't miss it scrolled off-screen.
 */
function HintPopover({ open, onToggle, text, label }: {
  open: boolean;
  onToggle: () => void;
  text: string;
  label: string;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; arrowLeft: number; below: boolean } | null>(null);

  // (c) useLayoutEffect measures button/tooltip DOM to position the popover
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- DOM measure for tooltip position
    if (!open) { setPos(null); return; }
    const place = () => {
      const btn = btnRef.current;
      const tip = tipRef.current;
      if (!btn) return;
      const b = btn.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = 10;        // viewport edge padding
      const gap = 10;           // space between button and tooltip
      const tw = tip?.offsetWidth ?? Math.min(260, vw * 0.72);
      const th = tip?.offsetHeight ?? 80;
      // Vertical: prefer above; flip below if not enough room above.
      const below = b.top - gap - th < margin;
      const top = below ? b.bottom + gap : b.top - gap - th;
      // Horizontal: center on the button, then clamp to viewport.
      const btnCenter = b.left + b.width / 2;
      let left = btnCenter - tw / 2;
      left = Math.max(margin, Math.min(left, vw - margin - tw));
      const arrowLeft = Math.max(12, Math.min(btnCenter - left, tw - 12));
      setPos({ left, top: Math.max(margin, Math.min(top, vh - margin - th)), arrowLeft, below });
    };
    // Two-pass: first render to measure real size, then place precisely.
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, text]);

  return (
    <>
      <button
        ref={btnRef}
        aria-label={label}
        aria-expanded={open}
        onClick={onToggle}
        className={cn(
          'w-[46px] min-w-[46px] cursor-pointer rounded-[14px] border border-solid text-[18px] font-bold',
          open
            ? 'border-brand bg-brand-alpha-15 text-brand'
            : 'border-white-alpha-20 bg-white-alpha-4 text-text-steel',
        )}
      >
        ?
      </button>

      {open && createPortal(
        <>
          {/* click-outside catcher */}
          <div onClick={onToggle} className="fixed inset-0 z-[10060]" />
          <div
            ref={tipRef}
            role="tooltip"
            className="fixed z-[10061] w-[min(260px,72vw)] rounded-md border border-white-alpha-12 bg-lighthouse-modal-accent-2 px-[13px] py-[11px] text-[13px] leading-[1.5] text-lighthouse-modal-text-2 shadow-[0_12px_30px_var(--color-black-alpha-50)]"
            style={{
              left: pos ? `${pos.left}px` : '-9999px',
              top: pos ? `${pos.top}px` : '-9999px',
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {text}
            {/* arrow pointing toward the button */}
            {pos && (
              <span
                className="absolute -translate-x-1/2"
                style={{
                  left: `${pos.arrowLeft}px`,
                  width: 0, height: 0,
                  borderLeft: '7px solid transparent',
                  borderRight: '7px solid transparent',
                  ...(pos.below
                    ? { bottom: '100%', borderBottom: `8px solid ${TOOLTIP_BG}` }
                    : { top: '100%', borderTop: `8px solid ${TOOLTIP_BG}` }),
                }}
              />
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

/**
 * Owner identity chip: small avatar + greedy display name (first+last -> name ->
 * @username -> ID), reusing the shared formatUserName helper (same as TapScreen).
 * Tapping the name reveals the @login when there's both a name and a username.
 */
function OwnerBadge({ owner }: { owner: OwnerInfo }) {
  const { t } = useTranslation();
  const { shortName, usernameFormatted, fallbackLetter } = formatUserName(owner);
  const [showLogin, setShowLogin] = useState(false);
  const canToggle = !!usernameFormatted && !!shortName;
  const primary = shortName || usernameFormatted || t('Player');
  const label = showLogin && usernameFormatted ? usernameFormatted : primary;

  const handleAvatarClick = owner.username
    ? () => openTelegramProfile(owner.username)
    : undefined;

  return (
    <span className="inline-flex items-center gap-sm">
      {owner.photo_url ? (
        <img
          src={owner.photo_url}
          alt=""
          onClick={handleAvatarClick}
          className={cn(
            'h-[18px] w-[18px] shrink-0 rounded-full border border-brand-alpha-60 object-cover',
            handleAvatarClick ? 'cursor-pointer' : 'cursor-default',
          )}
        />
      ) : (
        <span
          aria-hidden
          onClick={handleAvatarClick}
          className={cn(
            'inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold text-lighthouse-modal-text',
            handleAvatarClick ? 'cursor-pointer' : 'cursor-default',
          )}
          style={{
            background: `linear-gradient(135deg, ${TEAL}, var(--color-lighthouse-modal-surface))`,
          }}
        >
          {fallbackLetter.toUpperCase()}
        </span>
      )}
      <strong
        onClick={canToggle ? () => setShowLogin((v) => !v) : undefined}
        title={canToggle ? t('Show username') : undefined}
        className={cn(
          'font-bold text-white underline-offset-[3px]',
          canToggle
            ? 'cursor-pointer underline decoration-dotted decoration-white-alpha-35'
            : 'cursor-default no-underline',
        )}
      >
        {label}
      </strong>
    </span>
  );
}

export default function LighthouseModal(props: LighthouseModalProps) {
  const { t } = useTranslation();
  const {
    isOpen, onClose, priceStars, owner, isOwned, isMine,
    onBuy, buyLoading, hintOpen, onToggleHint,
    listing, sellOpen, onOpenSell, sellPrice, onChangeSellPrice, onList, sellLoading, onCancelListing,
    rate, buyCurrency, onSetBuyCurrency, onWalletBuy, walletBuyLoading, walletConnected,
  } = props;

  if (!isOpen) return null;

  // --- Action zone -------------------------------------------------------
  let actionZone: ReactNode = null;

  if (!isOwned) {
    // Unowned: golden price plaque + Buy + "?" hint
    actionZone = (
      <div className="flex flex-col gap-lg">
        <div className="flex items-center justify-center gap-md rounded-[14px] border border-lighthouse-aura-alpha-35 bg-[linear-gradient(135deg,var(--color-lighthouse-aura-alpha-16),var(--color-lighthouse-aura-alpha-5))] p-lg">
          <span className="[font-family:'Rubik_One',sans-serif] text-[26px] leading-none text-lighthouse-modal-accent">
            {priceStars.toLocaleString()}
          </span>
          <span className="text-[22px] leading-none">⭐</span>
        </div>

        <div className="flex items-stretch gap-md">
          <button
            disabled={buyLoading}
            onClick={onBuy}
            className={cn(ctaClassName, 'flex-1 shadow-[0_6px_18px_var(--color-lighthouse-aura-alpha-28)]')}
            style={{
              background: `linear-gradient(135deg, ${GOLD}, var(--color-lighthouse-modal-surface-2))`,
              opacity: buyLoading ? 0.6 : 1,
            }}
          >
            {t('Buy')}
          </button>
          <HintPopover
            open={hintOpen}
            onToggle={onToggleHint}
            label={t('Help')}
            text={t('You can become the owner of the lighthouse. This property is decorative. You can resell it to other players.')}
          />
        </div>
      </div>
    );
  } else if (isMine && !listing && !sellOpen) {
    actionZone = (
      <button
        onClick={onOpenSell}
        className={cn(ctaClassName, 'text-white')}
        style={{ background: `linear-gradient(135deg, ${TEAL}, var(--color-lighthouse-modal-surface))` }}
      >
        {t('Sell')}
      </button>
    );
  } else if (isMine && sellOpen && !listing) {
    actionZone = (
      <div className="flex flex-col gap-[10px]">
        <input
          type="text"
          inputMode="decimal"
          value={sellPrice}
          onChange={(e) => onChangeSellPrice(e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
          placeholder={t('Price in USDT')}
          className="box-border w-full rounded-md border border-lighthouse-modal-border bg-white-alpha-4 p-[13px] text-[15px] text-white"
        />
        <button
          disabled={sellLoading}
          onClick={onList}
          className={cn(ctaClassName, 'text-white')}
          style={{
            background: `linear-gradient(135deg, ${TEAL}, var(--color-lighthouse-modal-surface))`,
            opacity: sellLoading ? 0.6 : 1,
          }}
        >
          {t('List for sale')}
        </button>
      </div>
    );
  } else if (isMine && listing) {
    actionZone = (
      <div className="flex flex-col gap-[10px] text-center">
        <div className="text-[14px] text-text-light">
          {t('Listed for sale')}: ${listing.price_usdt} ({listing.price_ton.toFixed(2)} TON)
        </div>
        <button
          disabled={sellLoading}
          onClick={onCancelListing}
          className="w-full cursor-pointer rounded-md border border-lighthouse-modal-border bg-transparent p-[13px] text-[14px] font-semibold text-text-light"
        >
          {t('Cancel listing')}
        </button>
      </div>
    );
  } else if (isOwned && !isMine && listing && rate) {
    actionZone = (
      <div className="flex flex-col gap-[10px]">
        <div className="rounded-md bg-white-alpha-4 p-[10px] text-center text-[18px] font-extrabold text-white">
          ${listing.price_usdt} <span className="text-[15px] font-semibold text-text-steel">({listing.price_ton.toFixed(2)} TON)</span>
        </div>
        <div className="flex gap-md">
          {(['ton', 'usdt'] as const).map((c) => (
            <button
              key={c}
              onClick={() => onSetBuyCurrency(c)}
              className={cn(
                'flex-1 cursor-pointer rounded-[10px] p-[10px] font-bold text-white',
                buyCurrency === c
                  ? 'border border-brand bg-brand-alpha-15'
                  : 'border border-lighthouse-modal-border bg-transparent',
              )}
            >
              {c === 'ton' ? 'TON' : '$ USDT'}
            </button>
          ))}
        </div>
        <button
          disabled={walletBuyLoading}
          onClick={onWalletBuy}
          className={cn(ctaClassName, 'bg-[image:var(--gradient-ton-action-angled)] text-white')}
          style={{
            opacity: walletBuyLoading ? 0.6 : 1,
          }}
        >
          {walletConnected ? t('Pay with wallet') : t('Connect wallet')}
        </button>
      </div>
    );
  }

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[10010] flex items-center justify-center bg-lighthouse-backdrop-alpha-78 px-xl"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-[392px] overflow-y-auto rounded-2xl border border-white-alpha-6 bg-surface-base shadow-[0_24px_60px_var(--color-black-alpha-60)]"
      >
        {/* Close */}
        <button
          onClick={onClose}
          aria-label={t('Close')}
          className="absolute top-lg right-lg z-[2] h-[34px] w-[34px] cursor-pointer rounded-full border-none bg-lighthouse-panel-alpha-55 text-[20px] leading-[34px] text-white backdrop-blur-[6px]"
        >
          ×
        </button>

        {/* HERO: lighthouse over a night-ocean glow */}
        <div
          className="relative h-[210px] overflow-hidden rounded-t-2xl"
          style={{
            background: `linear-gradient(180deg, var(--color-lighthouse-modal-surface-3) 0%, ${CARD_BG} 100%)`,
          }}
        >
          {/* 5.1 — ONE coherent halo: a single soft glow radiating from the lighthouse lamp.
              No second light source — the sprite itself carries only a neutral depth shadow. */}
          <div
            aria-hidden
            className="pointer-events-none absolute top-[18px] left-1/2 h-[210px] w-[260px] -translate-x-1/2 blur-[6px] bg-[radial-gradient(ellipse_50%_48%_at_50%_42%,var(--color-lighthouse-aura-alpha-38)_0%,var(--color-lighthouse-aura-alpha-12)_40%,transparent_72%)]"
          />

          {/* 5.2 — fullscreen-preview + magnifier via shared component */}
          <FullscreenAssetViewer
            src={getDecorImagePath('decor_lighthouse')}
            alt={t('Lighthouse')}
            wrapperStyle={{
              position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', zIndex: 2,
            }}
            thumbnailStyle={{
              height: '188px', width: 'auto',
              filter: 'drop-shadow(0 10px 18px var(--color-black-alpha-45))',
              cursor: 'zoom-in',
            }}
            magnifierStyle={{ right: 2, top: 8 }}
          />
          {/* fade into card */}
          <div
            className="pointer-events-none absolute right-0 bottom-0 left-0 z-[2] h-[70px]"
            style={{
              background: `linear-gradient(180deg, transparent, ${CARD_BG})`,
            }}
          />
        </div>

        {/* BODY */}
        <div className="flex flex-col gap-[18px] px-[22px] pt-xs pb-[26px]">
          {/* Title + owner */}
          <div className="flex flex-col gap-md text-center">
            <div className="[font-family:'Rubik_One',sans-serif] text-[23px] leading-[1.15] text-white">
              {t('Ancient Lighthouse')}
            </div>
            <div
              className={cn(
                'inline-flex items-center gap-sm self-center rounded-pill border border-solid px-lg py-xs text-[12px]',
                isOwned
                  ? 'border-brand-alpha-40 bg-brand-alpha-15 text-brand'
                  : 'border-white-alpha-10 bg-white-alpha-5 text-text-steel',
              )}
            >
              <span className="opacity-80">{t('Lighthouse owner:')}</span>
              {isOwned && owner ? (
                <OwnerBadge owner={owner} />
              ) : (
                <strong
                  className={cn(
                    'font-bold',
                    isOwned ? 'text-white' : 'text-text-steel',
                  )}
                >
                  {isOwned ? t('Player') : t('Absent')}
                </strong>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-col gap-[10px] text-center">
            <p className="m-0 text-center text-[13.5px] leading-[1.6] text-text-lighthouse-primary">
              {t('For centuries, this lighthouse has guided lost ships through treacherous waters. Its light never fades, even in the darkest storms.')}
            </p>
            <p className="m-0 text-center text-[13.5px] leading-[1.6] text-text-lighthouse-secondary">
              {t('Legend says that those who climb to the top at midnight can see the entire island from above — and perhaps discover secrets that daylight hides.')}
            </p>
          </div>

          {/* divider */}
          <div className="h-px bg-white-alpha-7" />

          {/* Action zone */}
          {actionZone}
        </div>
      </div>
    </div>,
    document.body,
  );
}
